import { useEffect, useMemo, useState } from "react";
import { supabase } from "../supabaseClient";
import { TERMS_VERSION } from "../legal/terms_v1.jsx";
import { useAuth } from "../contexts/AuthContext";

export function useTermsAcceptance() {
  const [loading, setLoading] = useState(true);
  const [acceptedVersion, setAcceptedVersion] = useState(null);
  const [error, setError] = useState(null);
  const { user } = useAuth() || {};

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        if (!supabase || !user?.id) {
          if (active) {
            setAcceptedVersion(null);
            setLoading(false);
          }
          return;
        }

        const { data, error: profileErr } = await supabase
          .from("profiles")
          .select("accepted_terms_version, accepted_terms_at")
          .eq("id", user.id)
          .maybeSingle();
        if (profileErr) throw profileErr;

        if (active) {
          setAcceptedVersion(data?.accepted_terms_version || null);
          setLoading(false);
        }
      } catch (err) {
        console.error("[terms] load error", err);
        if (active) {
          setError(err);
          setLoading(false);
        }
      }
    };
    load();
    return () => {
      active = false;
    };
  }, [user?.id]);

  const needsAcceptance = useMemo(
    () => !!user && !loading && acceptedVersion !== TERMS_VERSION,
    [user, loading, acceptedVersion]
  );

  const acceptTerms = async () => {
    if (!supabase || !user?.id) {
      setAcceptedVersion(TERMS_VERSION);
      return;
    }
    try {
      const { error: updateErr } = await supabase
        .from("profiles")
        .upsert(
          {
            id: user.id,
            email: user.email || null,
            accepted_terms_version: TERMS_VERSION,
            accepted_terms_at: new Date().toISOString(),
          },
          { onConflict: "id" }
        );
      if (updateErr) {
        console.error("[terms] accept update error", updateErr);
        setError(updateErr);
        // Still allow closing so users are not blocked in UI
      }
    } catch (err) {
      console.error("[terms] accept exception", err);
      setError(err);
      // Still proceed to mark accepted locally
    }
    setAcceptedVersion(TERMS_VERSION);
  };

  return { loading, acceptedVersion, needsAcceptance, acceptTerms, error };
}
